import { Subscription, supabase } from '../lib/supabase';
import { getRenewalReminderHTML, getRenewalReminderText, getMultipleRenewalsHTML, EmailData } from './emailTemplates';

export interface EmailReminderData {
  subscription: Subscription;
  daysUntilRenewal: number;
  userEmail: string;
  userName?: string;
}

export interface MultipleEmailReminderData {
  subscriptions: Subscription[];
  daysUntilRenewal: number;
  userEmail: string;
  userName?: string;
}

// Send email via Nodemailer (new function)
export const sendEmailViaNodemailer = async (
  to: string,
  subject: string,
  html: string,
  text: string,
  type: string,
  subscriptionIds?: string[]
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('send-email-nodemailer', {
      body: {
        to,
        subject,
        html,
        text,
        type,
        subscriptionIds: subscriptionIds || [],
      },
    });

    if (error) {
      throw new Error(`Nodemailer email sending failed: ${error.message}`);
    }

    if (data?.success) {
      console.log(`✅ Nodemailer email sent successfully: ${data.messageId}`);
      return true;
    } else {
      throw new Error(`Nodemailer error: ${data?.error || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('❌ Failed to send email via Nodemailer:', error);
    return false;
  }
};

// Send single subscription reminder via Nodemailer
export const sendRenewalReminder = async (data: EmailReminderData): Promise<boolean> => {
  try {
    const emailData: EmailData = {
      subscription: data.subscription,
      daysUntilRenewal: data.daysUntilRenewal,
      userEmail: data.userEmail,
      userName: data.userName,
    };

    const htmlContent = getRenewalReminderHTML(emailData);
    const textContent = getRenewalReminderText(emailData);

    // Use Nodemailer instead of SendGrid
    const success = await sendEmailViaNodemailer(
      data.userEmail,
      `🔔 ${data.subscription.name} renews in ${data.daysUntilRenewal} day${data.daysUntilRenewal === 1 ? '' : 's'}`,
      htmlContent,
      textContent,
      'renewal_reminder',
      [data.subscription.id]
    );

    if (success) {
      console.log(`✅ Renewal reminder sent via Nodemailer for ${data.subscription.name}`);
    }
    return success;
  } catch (error) {
    console.error('❌ Failed to send renewal reminder:', error);
    return false;
  }
};

// Send multiple subscriptions reminder via Nodemailer
export const sendMultipleRenewalsReminder = async (data: MultipleEmailReminderData): Promise<boolean> => {
  try {
    const htmlContent = getMultipleRenewalsHTML(
      data.subscriptions,
      data.daysUntilRenewal,
      data.userEmail,
      data.userName
    );

    // Use Nodemailer instead of SendGrid
    const success = await sendEmailViaNodemailer(
      data.userEmail,
      `🔔 ${data.subscriptions.length} subscription${data.subscriptions.length === 1 ? '' : 's'} renewing in ${data.daysUntilRenewal} day${data.daysUntilRenewal === 1 ? '' : 's'}`,
      htmlContent,
      `You have ${data.subscriptions.length} subscription(s) renewing in ${data.daysUntilRenewal} day(s). Check your dashboard for details.`,
      'multiple_renewals',
      data.subscriptions.map(sub => sub.id)
    );

    if (success) {
      console.log(`✅ Multiple renewals reminder sent via Nodemailer for ${data.subscriptions.length} subscriptions`);
    }
    return success;
  } catch (error) {
    console.error('❌ Failed to send multiple renewals reminder:', error);
    return false;
  }
};

// Check if subscription needs reminder today
export const shouldSendReminder = (
  subscription: Subscription,
  reminderDaysBefore: number
): boolean => {
  const today = new Date();
  const renewalDate = new Date(subscription.next_renewal_date);
  const reminderDate = new Date(renewalDate);
  reminderDate.setDate(renewalDate.getDate() - reminderDaysBefore);

  // Check if today is the reminder date
  return (
    today.getFullYear() === reminderDate.getFullYear() &&
    today.getMonth() === reminderDate.getMonth() &&
    today.getDate() === reminderDate.getDate()
  );
};

// Get subscriptions that need reminders today
export const getSubscriptionsNeedingReminders = (
  subscriptions: Subscription[],
  reminderDaysBefore: number
): Subscription[] => {
  return subscriptions.filter(subscription => 
    shouldSendReminder(subscription, reminderDaysBefore)
  );
};

// Group subscriptions by user for batch email sending
export const groupSubscriptionsByUser = (
  subscriptions: Subscription[],
  reminderDaysBefore: number
): Map<string, Subscription[]> => {
  const userGroups = new Map<string, Subscription[]>();

  subscriptions.forEach(subscription => {
    if (shouldSendReminder(subscription, reminderDaysBefore)) {
      const userId = subscription.user_id;
      if (!userGroups.has(userId)) {
        userGroups.set(userId, []);
      }
      userGroups.get(userId)!.push(subscription);
    }
  });

  return userGroups;
};

// Send daily reminder digest
export const sendDailyReminderDigest = async (
  userEmail: string,
  userName: string | undefined,
  subscriptions: Subscription[],
  reminderDaysBefore: number
): Promise<boolean> => {
  const subscriptionsNeedingReminders = getSubscriptionsNeedingReminders(
    subscriptions,
    reminderDaysBefore
  );

  if (subscriptionsNeedingReminders.length === 0) {
    console.log('No subscriptions need reminders today');
    return true;
  }

  if (subscriptionsNeedingReminders.length === 1) {
    // Send single subscription reminder
    return await sendRenewalReminder({
      subscription: subscriptionsNeedingReminders[0],
      daysUntilRenewal: reminderDaysBefore,
      userEmail,
      userName,
    });
  } else {
    // Send multiple subscriptions reminder
    return await sendMultipleRenewalsReminder({
      subscriptions: subscriptionsNeedingReminders,
      daysUntilRenewal: reminderDaysBefore,
      userEmail,
      userName,
    });
  }
}; 